import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Columns3, ExternalLink, FlaskConical, Play, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { displayCategoryName } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui";
import {
  useChangeRequestList,
  useChangeRequestModelContext,
  useChangeRequestProcessing,
} from "~/features/changeRequests/hooks";
import type {
  ChangeRequestListFilters,
  ChangeRequestRow,
  ChangeRequestSortOrder,
  ChangeRequestSummary,
  SortDirection,
} from "~/features/changeRequests/types";
import { toChangeRequestRowList } from "~/features/changeRequests/utils/transformers";
import { v2Route } from "~/v2/routes";
import { BatchReviewDialog } from "./BatchReviewDialog";
import { ChangeRequestStatusBadge } from "./ChangeRequestStatusBadge";

interface ChangeRequestsTableProps {
  filters: ChangeRequestListFilters;
  sortOrder: ChangeRequestSortOrder;
  direction: SortDirection;
  onSortingChange: (order: ChangeRequestSortOrder, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  /** When set, rows become clickable and open the change request (peek/detail). */
  onSelect?: (id: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, ChangeRequestRow>();

/** Column ids that map 1:1 to a sortable `ChangeRequestSortOrder`. */
const SORTABLE_COLUMNS = new Set(["status", "comments", "createdAt"]);

/** Hideable data columns (the select column is always shown). Order mirrors V1. */
const CR_COLUMNS = [
  { id: "job", labelKey: "ui.job" },
  { id: "composer", labelKey: "ui.composer" },
  { id: "jobRun", labelKey: "ui.jobRun" },
  { id: "status", labelKey: "model.changeRequest.status" },
  { id: "comments", labelKey: "ui.comments" },
  { id: "summary", labelKey: "ui.summary" },
  { id: "createdAt", labelKey: "model.common.createdAt" },
] as const;

type BadgeTone = React.ComponentProps<typeof Badge>["tone"];

// Model-type / operation chip tones mirror V1's colours.
const MODEL_TYPE_TONE: Record<string, BadgeTone> = {
  entity: "score-b",
  relation: "status-approved",
  recordMetadata: "score-c",
};
const OPERATION_TONE: Record<string, BadgeTone> = {
  create: "status-draft",
  update: "status-processing",
};

/** A cell's name as text + a trailing external-link icon (icon stops row-click propagation). */
function CellLink({ name, to, label }: { name: string; to: string | null; label: string }) {
  return (
    <span className="flex items-start gap-1.5">
      <span className="min-w-0 break-words text-14-regular text-fg-2">{name}</span>
      {to && (
        <Link
          to={to}
          className="mt-0.5 shrink-0 text-fg-link hover:text-primary-700"
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      )}
    </span>
  );
}

function SummaryCell({ summary }: { summary: ChangeRequestSummary }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {summary.modelTypeLabel && (
          <Badge tone={MODEL_TYPE_TONE[summary.modelType] ?? "default"}>{summary.modelTypeLabel}</Badge>
        )}
        {summary.operationType && (
          <Badge tone={OPERATION_TONE[summary.operationType] ?? "default"}>{summary.operationType}</Badge>
        )}
        {summary.categoryNames && <span className="text-12-regular text-fg-3">{summary.categoryNames}</span>}
      </div>
      {summary.headline && (
        <span className="break-words text-14-regular font-medium text-fg-1">{summary.headline}</span>
      )}
      {summary.secondaryProps.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {summary.secondaryProps.map((prop) => (
            <span key={prop.key} className="text-12-regular text-fg-3">
              <span className="text-fg-disabled">{prop.key}:</span> {prop.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChangeRequestsTable({
  filters,
  sortOrder,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
}: ChangeRequestsTableProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { data, refetch } = useChangeRequestList({ filters, sortOrder, direction, pageIndex, pageSize });
  const { categories } = useChangeRequestModelContext();
  const { trackJob, isProcessing } = useChangeRequestProcessing();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<{
    dryRun: boolean;
    ids: string[];
    batchJobId: string;
    batchJobRunId: string | null;
    batchJobName: string | null;
  } | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAllColumns = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(CR_COLUMNS.map((c) => [c.id, next])));

  const categoryLabelById = useMemo(
    () =>
      new Map(
        categories.map((c) => [
          c.id,
          displayCategoryName({ name: c.name, i18nLabel: c.i18nLabel as Record<string, unknown> }, locale),
        ]),
      ),
    [categories, locale],
  );

  const rows = useMemo(
    () => toChangeRequestRowList(data.changeRequests, categoryLabelById),
    [data.changeRequests, categoryLabelById],
  );
  const totalCount = data.changeRequestsAggregate.aggregate?.totalCount ?? 0;
  const openCount = data.openChangeRequestsAggregate.aggregate?.count ?? 0;

  // Batch reviews are scoped to one job: a row is selectable only if it's open, belongs to a job,
  // matches the job of any already-selected rows, and is not mid-processing.
  const selectedJobId = useMemo(() => {
    const first = rows.find((r) => selectedIds.has(r.id));
    return first?.job?.id ?? null;
  }, [rows, selectedIds]);
  const scopedJobId = filters.batchChangeRequestsJobId ?? null;
  const targetJobId = selectedJobId ?? scopedJobId;

  const isSelectable = (row: ChangeRequestRow): boolean =>
    row.status === "open" &&
    row.job !== null &&
    !isProcessing(row.id) &&
    (selectedJobId === null || row.job.id === selectedJobId);

  const toggleRow = (row: ChangeRequestRow) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  // Batch operations need an authoritative job scope. Selected rows provide one; otherwise rely on
  // the URL/deep-link job filter, matching V1 behaviour.
  const selectableRows = targetJobId
    ? rows.filter((r) => r.status === "open" && r.job?.id === targetJobId && !isProcessing(r.id))
    : [];
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id));
  const someSelected = selectableRows.some((r) => selectedIds.has(r.id));

  // Batch toolbar is always visible; buttons act on the selection (if any) else all open in the job.
  const batchJobId = targetJobId;
  const batchJobName = batchJobId ? (rows.find((r) => r.job?.id === batchJobId)?.job?.name ?? null) : null;
  const batchJobRunId = filters.batchChangeRequestsJobRunId ?? null;
  const scopedOpenCount = scopedJobId ? openCount : 0;
  const batchIds = selectedIds.size > 0 ? [...selectedIds] : [];
  const canBatch = Boolean(batchJobId) && (selectedIds.size > 0 || scopedOpenCount > 0);
  const batchDisabledHint = t("feature.changeRequests.batchReview.disabledHint");

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        for (const r of selectableRows) next.delete(r.id);
        return next;
      }
      return new Set([...prev, ...selectableRows.map((r) => r.id)]);
    });
  };

  const onCommitted = (jobRunId: string, ids: string[]) => {
    if (dialog) {
      trackJob(jobRunId, ids);
    }
    setSelectedIds(new Set());
    void refetch();
  };

  // Clear the selection whenever the visible set changes (page, size, or sort) — otherwise a batch
  // action could run against off-screen selected rows the user can no longer see. Sorting resets to
  // page 0 and reorders the list, so selected rows can move out of view just like a page change
  // (V1 cleared selection on every list-shaping change; see useChangeRequestsColumns).
  const handlePageIndexChange = (next: number) => {
    setSelectedIds(new Set());
    onPageIndexChange(next);
  };
  const handlePageSizeChange = (next: number) => {
    setSelectedIds(new Set());
    onPageSizeChange(next);
  };

  const sorting: SortingState = useMemo(() => [{ id: sortOrder, desc: direction === "desc" }], [sortOrder, direction]);

  const handleSortingChange = (next: SortingState) => {
    setSelectedIds(new Set());
    const first = next[0];
    if (!first || !SORTABLE_COLUMNS.has(first.id)) {
      onSortingChange("updatedAt", "desc");
      return;
    }
    onSortingChange(first.id as ChangeRequestSortOrder, first.desc ? "desc" : "asc");
  };

  const columns: ColumnDef<DataTableFeatures, ChangeRequestRow, any>[] = [
    helper.display({
      id: "select",
      meta: { width: 40 },
      header: () => (
        <Checkbox
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          disabled={selectableRows.length === 0}
          onCheckedChange={toggleAll}
          aria-label={t("ui.selectAll")}
        />
      ),
      cell: (info) => {
        const row = info.row.original;
        return (
          <Checkbox
            checked={selectedIds.has(row.id)}
            disabled={!isSelectable(row)}
            onCheckedChange={() => toggleRow(row)}
            onClick={(e) => e.stopPropagation()}
            aria-label={t("ui.select")}
          />
        );
      },
    }),
    helper.display({
      id: "job",
      meta: { width: 160, cellClassName: "whitespace-normal break-words align-top" },
      header: () => t("ui.job"),
      enableSorting: false,
      cell: (info) => {
        const { job } = info.row.original;
        if (!job) return <span className="text-fg-3">—</span>;
        const to = job.dipId
          ? `/data-import-projects/${job.dipId}?peekType=batchChangeRequestsJob&peekId=${job.id}`
          : null;
        return <CellLink name={job.name} to={to} label={t("ui.view")} />;
      },
    }),
    helper.display({
      id: "composer",
      meta: { width: 170, cellClassName: "whitespace-normal break-words align-top" },
      header: () => t("ui.composer"),
      enableSorting: false,
      cell: (info) => {
        const { composer } = info.row.original;
        if (!composer) return <span className="text-fg-3">—</span>;
        // Composers are peek-only: deep-link into their DIP pipeline with the peek open.
        // A composer with no DIP has nowhere to open, so it renders without a link.
        const peekType =
          composer.kind === "entity" ? "entityComposer" : composer.kind === "relation" ? "relationComposer" : null;
        const to =
          composer.dipId && peekType
            ? v2Route.dataImportProjects.detail.$buildPath({
                params: { id: composer.dipId },
                searchParams: { peekType, peekId: composer.id },
              })
            : null;
        return <CellLink name={composer.name} to={to} label={t("ui.view")} />;
      },
    }),
    helper.display({
      id: "jobRun",
      meta: { width: 120, cellClassName: "align-top" },
      header: () => t("ui.jobRun"),
      enableSorting: false,
      cell: (info) => {
        const { jobRunAt } = info.row.original;
        return jobRunAt ? (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {t("date.long", { date: parseISO(jobRunAt) })}
          </span>
        ) : (
          <span className="text-fg-3">—</span>
        );
      },
    }),
    helper.accessor("status", {
      id: "status",
      meta: { width: 120, cellClassName: "align-top" },
      header: () => t("model.changeRequest.status"),
      cell: (info) => (
        <ChangeRequestStatusBadge status={info.getValue()} processing={isProcessing(info.row.original.id)} />
      ),
    }),
    helper.accessor("commentsCount", {
      id: "comments",
      meta: { width: 116, cellClassName: "align-top" },
      header: () => t("ui.comments"),
      cell: (info) => <span className="tabular-nums text-fg-2">{info.getValue()}</span>,
    }),
    helper.display({
      id: "summary",
      meta: { width: 360, cellClassName: "whitespace-normal break-words align-top" },
      header: () => t("ui.summary"),
      enableSorting: false,
      cell: (info) => <SummaryCell summary={info.row.original.summary} />,
    }),
    helper.accessor("createdAt", {
      id: "createdAt",
      meta: { width: 140, cellClassName: "align-top whitespace-nowrap" },
      header: () => t("model.common.createdAt"),
      cell: (info) => (
        <span className="whitespace-nowrap tabular-nums text-fg-2">
          {t("date.long", { date: parseISO(info.getValue()) })}
        </span>
      ),
    }),
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-fg-2">
            {selectedIds.size > 0
              ? t("feature.changeRequests.batchReview.selected", { count: selectedIds.size })
              : t("feature.changeRequests.batchReview.jobOpenCount", { count: scopedOpenCount })}
          </span>
          {selectedIds.size > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              {t("ui.clearAll")}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canBatch}
            title={canBatch ? undefined : batchDisabledHint}
            onClick={() =>
              batchJobId && setDialog({ dryRun: true, ids: batchIds, batchJobId, batchJobRunId, batchJobName })
            }
          >
            <FlaskConical />
            <span>{t("feature.changeRequests.batchReview.dryRun")}</span>
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={!canBatch}
            title={canBatch ? undefined : batchDisabledHint}
            onClick={() =>
              batchJobId && setDialog({ dryRun: false, ids: batchIds, batchJobId, batchJobRunId, batchJobName })
            }
          >
            <Play />
            <span>{t("feature.changeRequests.batchReview.runJob")}</span>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t("ui.reload")}
            onClick={() => void refetch()}
          >
            <RotateCw />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
                <Columns3 />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onSelect={() => toggleAllColumns(false)}>{t("ui.hideAll")}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => toggleAllColumns(true)}>{t("ui.showAll")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {CR_COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={isVisible(col.id)}
                  onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {t(col.labelKey)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <RemoteDataTable<ChangeRequestRow>
        columns={columns}
        data={rows}
        rowCount={totalCount}
        pageIndex={pageIndex}
        onPageIndexChange={handlePageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        isPending={isPending}
        layout="fixed"
        onRowClick={onSelect ? (row) => onSelect(row.id) : undefined}
        emptyMessage={t("feature.changeRequests.empty")}
      />

      {dialog && (
        <BatchReviewDialog
          open
          onOpenChange={(next) => !next && setDialog(null)}
          dryRun={dialog.dryRun}
          batchJobId={dialog.batchJobId}
          batchJobRunId={dialog.batchJobRunId}
          batchJobName={dialog.batchJobName}
          selectedIds={dialog.ids}
          onCommitted={onCommitted}
          onOpenChangeRequest={onSelect}
        />
      )}
    </div>
  );
}
